import { useState } from "react";
import "./newUser.css";
import { register } from "../../redux/apiCalls";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff, PersonAdd, Save } from "@material-ui/icons";

export default function NewUser() {
    const currentUser = useSelector((state) => state.user.currentUser);
    const [inputs, setInputs] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleChange = (e) => {
        setInputs((prev) => {
            return { ...prev, [e.target.name]: e.target.value };
        });
        // Clear messages when user starts typing
        if (errorMessage) setErrorMessage("");
        if (successMessage) setSuccessMessage("");
    };

    const handleChangeAddress = (e) => {
        const { name, value } = e.target;
        setInputs((prev) => ({
            ...prev,
            address: {
                ...(prev.address || {}),
                [name]: value,
            },
        }));
        if (errorMessage) setErrorMessage("");
        if (successMessage) setSuccessMessage("");
    };

    const handleClick = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        const user = { ...inputs, createdBy: currentUser?._id };

        try {
            await register({ user });
            setSuccessMessage("User created successfully!");
            setInputs({}); // Reset form
        } catch (err) {
            console.log(err);
            setErrorMessage("Failed to create user. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden new-user-page">
            {/* Luxury Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-purple-400/10 to-pink-400/10 rounded-full blur-3xl animate-pulse delay-500"></div>
            </div>

            <div className="relative z-10 max-w-5xl mx-auto p-6 page-container">
                {/* Premium Glass Container */}
                <div className="bg-gray-200 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-10 relative overflow-hidden form-shell">
                    {/* Luxury Header Overlay */}
                    <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-amber-600/10 blur-xl"></div>

                    {/* Ultra-Premium Header */}
                    <div className="relative z-10 mb-12 header-block">
                        <div className="text-center mb-8">
                            <div className="relative inline-block group">
                                {/* Animated Rings */}
                                <div className="absolute inset-0 rounded-full">
                                    <div className="absolute inset-0 rounded-full border-2 border-blue-500/30 animate-ping"></div>
                                    <div
                                        className="absolute inset-2 rounded-full border-2 border-purple-500/40 animate-ping"
                                        style={{ animationDelay: "0.5s" }}
                                    ></div>
                                    <div
                                        className="absolute inset-4 rounded-full border-2 border-amber-500/50 animate-ping"
                                        style={{ animationDelay: "1s" }}
                                    ></div>
                                </div>

                                {/* Central Icon */}
                                <div className="relative w-24 h-24 bg-gradient-to-br from-blue-500 via-purple-600 to-amber-500 rounded-full flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-700 shadow-2xl">
                                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full"></div>
                                    <PersonAdd className="text-white text-4xl relative z-10" />
                                </div>
                            </div>

                            {/* Luxury Status Bar */}
                            <div className="flex items-center justify-center gap-6 mt-8 status-row">
                                <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/50 shadow-lg status-pill">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-500/50"></div>
                                    <span className="text-sm font-bold text-emerald-700 tracking-wide">
                                        SYSTEM ACTIVE
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/50 shadow-lg status-pill">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shadow-lg shadow-blue-500/50"></div>
                                    <span className="text-sm font-bold tracking-wide">
                                        SECURED CONNECTION
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Premium Success Message */}
                    {successMessage && (
                        <div className="mb-8 relative group animate-in slide-in-from-top duration-500">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-green-400/20 rounded-2xl blur-xl"></div>
                            <div className="relative bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200/50 rounded-2xl p-6 backdrop-blur-sm">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-emerald-500 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center">
                                            <span className="text-white text-xl">
                                                ✓
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-emerald-800 mb-1">
                                            Success!
                                        </h4>
                                        <p className="text-emerald-700 font-medium">
                                            {successMessage}
                                        </p>
                                    </div>
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Premium Error Message */}
                    {errorMessage && (
                        <div className="mb-8 relative group animate-in slide-in-from-top duration-500">
                            <div className="absolute inset-0 bg-gradient-to-r from-red-400/20 to-rose-400/20 rounded-2xl blur-xl"></div>
                            <div className="relative bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200/50 rounded-2xl p-6 backdrop-blur-sm">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-red-500 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center">
                                            <span className="text-white text-xl">
                                                !
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-lg font-bold text-red-800 mb-1">
                                            Error
                                        </h4>
                                        <p className="text-red-700 font-medium">
                                            {errorMessage}
                                        </p>
                                    </div>
                                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleClick} className="space-y-10">
                        {/* Premium Basic Information Section */}
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-indigo-400/10 rounded-3xl blur-xl"></div>
                            <div className="relative bg-black text-white backdrop-blur-sm rounded-3xl p-8 border-2 border-blue-200/30 shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:border-blue-300/50 section-card">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="relative">
                                        <div className="absolute inset-0 rounded-2xl blur-lg opacity-75 animate-pulse"></div>
                                        <div className="relative w-12 h-12 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                                            <span className="text-white text-xl">
                                                👤
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-black text-white mb-2">
                                            Basic Information
                                        </h3>
                                        <p className="text-blue-200 font-medium">
                                            Essential user details and
                                            authentication
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                                        <span className="text-blue-200 text-sm font-medium">
                                            Required
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Full Name{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="name"
                                                type="text"
                                                placeholder="Enter full name"
                                                value={inputs.name || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Login ID (Name with no space){" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="email"
                                                type="text"
                                                placeholder="Enter username"
                                                value={inputs.email || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Phone Number{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="phoneNumber"
                                                type="tel"
                                                placeholder="Enter phone number"
                                                value={inputs.phoneNumber || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            Email Address
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="emailAddress"
                                                type="email"
                                                placeholder="Enter email address"
                                                value={
                                                    inputs.emailAddress || ""
                                                }
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            Address Line One
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="lineOne"
                                                type="text"
                                                placeholder="Enter address line one"
                                                value={inputs.address?.lineOne || ""}
                                                onChange={handleChangeAddress}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            Address Line Two
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="lineTwo"
                                                type="text"
                                                placeholder="Enter address line two"
                                                value={inputs.address?.lineTwo || ""}
                                                onChange={handleChangeAddress}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            Zipcode
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="zip"
                                                type="text"
                                                placeholder="Enter zipcode"
                                                value={inputs.address?.zip || ""}
                                                onChange={handleChangeAddress}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            City
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="city"
                                                type="text"
                                                placeholder="Enter city"
                                                value={inputs.address?.city || ""}
                                                onChange={handleChangeAddress}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide group-hover:text-blue-300 transition-colors duration-200 text-center w-full block">
                                            Country
                                        </label>
                                        <div className="relative w-full">
                                            <input
                                                name="country"
                                                type="text"
                                                placeholder="Enter country"
                                                value={inputs.address?.country || ""}
                                                onChange={handleChangeAddress}
                                                className="w-full px-6 py-4 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                            />
                                        </div>
                                    </div>
                                    <div className="group space-y-3">
                                        <label className="text-sm font-bold text-white tracking-wide flex items-center gap-2 group-hover:text-blue-300 transition-colors duration-200">
                                            Password{" "}
                                            <span className="text-red-500 animate-pulse">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="password"
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                placeholder="Enter password"
                                                value={inputs.password || ""}
                                                onChange={handleChange}
                                                className="w-full px-6 py-4 pr-14 bg-white/70 backdrop-blur-sm border-2 border-slate-200/50 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100/50 transition-all duration-500 font-medium text-black placeholder-slate-400 hover:bg-white/90 hover:border-blue-300/70 hover:shadow-lg"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword
                                                    )
                                                }
                                                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-300"
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </button>
                                            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative pt-16">
                            <div className="flex justify-center">
                                <div className="relative group">
                                    <div className="absolute inset-0 rounded-3xl">
                                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500/20 to-purple-600/20 animate-ping"></div>
                                        <div
                                            className="absolute inset-2 rounded-3xl bg-gradient-to-r from-purple-500/30 to-amber-500/30 animate-ping"
                                            style={{ animationDelay: "0.5s" }}
                                        ></div>
                                        <div
                                            className="absolute inset-4 rounded-3xl bg-gradient-to-r from-amber-500/20 to-blue-500/20 animate-ping"
                                            style={{ animationDelay: "1s" }}
                                        ></div>
                                    </div>

                                    {/* Main Glow Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-purple-600 to-amber-500 rounded-3xl blur-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-700"></div>

                                    {/* Ultra-Premium Button */}
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`relative px-16 py-6 bg-black from-slate-900 via-blue-900 to-slate-900 hover:from-blue-900 hover:via-purple-900 hover:to-blue-900 text-white font-black text-xl rounded-3xl shadow-2xl transition-all duration-700 transform hover:scale-110 hover:shadow-3xl flex items-center gap-6 border-2 border-white/20 backdrop-blur-xl primary-action-btn ${
                                            isLoading
                                                ? "opacity-75 cursor-not-allowed scale-95"
                                                : "hover:border-amber-400/50"
                                        }`}
                                    >
                                        {isLoading ? (
                                            <>
                                                <div className="relative">
                                                    <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                    <div
                                                        className="absolute inset-0 w-8 h-8 border-4 border-transparent border-t-amber-400 rounded-full animate-spin"
                                                        style={{
                                                            animationDirection:
                                                                "reverse",
                                                            animationDuration:
                                                                "0.8s",
                                                        }}
                                                    ></div>
                                                </div>
                                                <span className="text-xl tracking-wider">
                                                    Creating User...
                                                </span>
                                                <div className="flex gap-1">
                                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                                    <div
                                                        className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"
                                                        style={{
                                                            animationDelay:
                                                                "0.2s",
                                                        }}
                                                    ></div>
                                                    <div
                                                        className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"
                                                        style={{
                                                            animationDelay:
                                                                "0.4s",
                                                        }}
                                                    ></div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-xl tracking-wider font-black bg-gradient-to-r from-white via-blue-100 to-amber-100 bg-clip-text text-transparent">
                                                    Create New User
                                                </span>
                                                <div className="flex gap-2">
                                                    <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse shadow-lg shadow-blue-400/50"></div>
                                                    <div
                                                        className="w-3 h-3 bg-purple-400 rounded-full animate-pulse shadow-lg shadow-purple-400/50"
                                                        style={{
                                                            animationDelay:
                                                                "0.3s",
                                                        }}
                                                    ></div>
                                                    <div
                                                        className="w-3 h-3 bg-amber-400 rounded-full animate-pulse shadow-lg shadow-amber-400/50"
                                                        style={{
                                                            animationDelay:
                                                                "0.6s",
                                                        }}
                                                    ></div>
                                                </div>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
